"use client";
import { memo, useEffect, useState, useMemo } from "react";
import { cn } from "utils";
import { VirtualRCTableView, Icon, ospThemes } from "@ospweb/sdk-ui";
import { Sentiment } from "../constants";
import defaultStyles from "./IndicatorTable.module.css";
import timelineStyles from "./IndicatorTable.timeline.module.css";
import modalStyles from "./IndicatorTable.modal.module.css";
import webModalStyles from "./IndicatorTable.web-modal.module.css";

import Translations from "@translate/components/DetailsCommon/Forecast/TechnicalSignals/IndicatorTable";
import { I18nUtil } from "@utils/i18n";
import { formatBigNumber } from "@utils/formatBigNumber";

const I18n = I18nUtil.createScoped(Translations);

interface IndicatorData {
  id?: string;
  indicator?: string;
  indicator_name?: string;
  signal?: string;
  value?: string | number;
  sentiment?: "Bullish" | "Bearish" | "Neutral";
}

interface IndicatorTableProps {
  data?: IndicatorData[];
  className?: string;
  /** 样式变体：default 为默认样式（带边框），timeline 为时间线场景样式（无边框） */
  variant?: "default" | "timeline" | "modal" | "web-modal";
}

// 情绪样式映射 - 移至组件外部作为常量
const sentimentStyle = {
  [Sentiment.BULLISH]: "text-content_accent1_primary",
  [Sentiment.BEARISH]: "text-content_accent2_primary",
  [Sentiment.NEUTRAL]: "text-content_tertiary",
};

const IndicatorTable = ({
  data,
  className,
  variant = "default",
}: IndicatorTableProps) => {
  const [viewportWidth, setViewportWidth] = useState<number>(0);

  // 根据 variant 选择样式
  const styles = useMemo(() => {
    switch (variant) {
      case "timeline":
        return timelineStyles;
      case "web-modal":
        return webModalStyles;
      case "modal":
        return modalStyles;
      default:
        return defaultStyles;
    }
  }, [variant]);

  // 滚动条渐变与外部滚动逻辑移除，使用 VirtualRCTableView 自带滚动

  // 监听窗口大小变化，仅用于 480 阈值的响应式列宽
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window?.innerWidth || 0);
    };

    // 初始化
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isOver480 = viewportWidth > 480;

  const timelineAndDefaultColumns = useMemo(
    () => [
      {
        title: I18n.t("Indicators"),
        dataIndex: "indicator",
        key: "indicator",
        align: "left",
        width:
          variant === "timeline"
            ? isOver480
              ? "36%"
              : 48
            : isOver480
              ? "40%"
              : 120,
        render: (_: unknown, record: IndicatorData) => (
          <div className="body_output_m whitespace-normal line-clamp-2 text-left">
            {record.indicator}
          </div>
        ),
      },
      {
        title: I18n.t("Signal"),
        dataIndex: "signal",
        key: "signal",
        align: "left",
        width:
          variant === "timeline"
            ? isOver480
              ? "36%"
              : 184
            : isOver480
              ? "40%"
              : 150,
        render: (value: IndicatorData["signal"]) => (
          <div className="body_output_m whitespace-normal line-clamp-2 text-left">
            {value}
          </div>
        ),
      },
      {
        title: I18n.t("Value"),
        dataIndex: "value",
        key: "value",
        align: "left",
        width: variant === "timeline" ? "13%" : isOver480 ? "10%" : 80,
        minWidth: variant === "timeline" ? 77 : undefined,
        // maxWidth: variant === "timeline" ? 77 : undefined,
        render: (value: IndicatorData["value"]) => (
          <div className="body_output_m whitespace-normal line-clamp-2 text-left">
            {variant === "timeline"
              ? value === "/" || value === "--"
                ? "/"
                : formatBigNumber(value, { precision: 4 })
              : value}
          </div>
        ),
      },
      {
        title: I18n.t("Sentiment"),
        dataIndex: "sentiment",
        key: "sentiment",
        align: "left",
        width: variant === "timeline" ? "15%" : isOver480 ? "10%" : 100,
        minWidth: variant === "timeline" ? 83 : undefined,
        render: (_: unknown, record: IndicatorData) => (
          <div className="flex justify-start">
            <div
              className={cn(
                "inline-flex items-center gap-[2px] rounded-[4px] py-[2px] pl-[4px] pr-[6px] body_output_m",
                sentimentStyle?.[
                  record.sentiment as keyof typeof sentimentStyle
                ],
              )}
            >
              <Icon
                name={
                  record.sentiment === "Bullish"
                    ? "ic_rise"
                    : record.sentiment === "Bearish"
                      ? "ic_fall"
                      : "minus"
                }
                size={14}
                color={
                  record.sentiment === "Bullish"
                    ? ospThemes.colors.content_accent1_primary
                    : record.sentiment === "Bearish"
                      ? ospThemes.colors.content_accent2_primary
                      : ospThemes.colors.content_tertiary
                }
              />
              <span>{record.sentiment}</span>
            </div>
          </div>
        ),
      },
    ],
    [isOver480, variant],
  );

  const modalColumns = useMemo(() => {
    const isDesktop = variant === "web-modal";
    return [
      {
        title: I18n.t("Indicators"),
        dataIndex: "indicator",
        key: "indicator",
        align: "left",
        width: isDesktop ? "25%" : "27.5%",
        render: (_: unknown, record: IndicatorData) => (
          <div className="body_medium_m text-content_primary text-ellipsis line-clamp-1 text-left">
            {record.indicator}
          </div>
        ),
      },
      {
        title: I18n.t("Signal"),
        dataIndex: "signal",
        key: "signal",
        align: "left",
        width: isDesktop ? "25%" : "51%",

        render: (value: IndicatorData["signal"]) => (
          <div className="body_medium_m text-content_primary text-ellipsis line-clamp-1 text-left">
            {value}
          </div>
        ),
      },
      {
        title: I18n.t("Value"),
        dataIndex: "value",
        key: "value",
        align: "left",
        width: isDesktop ? "25%" : "27.5%",
        render: (value: IndicatorData["value"]) => (
          <div className="body_medium_m text-content_primary text-ellipsis line-clamp-1 text-left">
            {value === "/" || value === "--"
              ? "--"
              : formatBigNumber(value, { precision: 4 })}
          </div>
        ),
      },
    ];
  }, [variant]);

  const webModalColumns = useMemo(() => {
    return [
      ...modalColumns,
      {
        title: I18n.t("Sentiment"),
        dataIndex: "sentiment",
        key: "sentiment",
        align: "left",
        width: "25%",
        render: (_: unknown, record: IndicatorData) => (
          <div className="flex justify-start w-full">
            <div
              className={cn(
                "w-full inline-flex max-h-[20px] items-center gap-[2px] rounded-[4px] py-[2px] pl-[4px] pr-[6px] body_bold_s",
                sentimentStyle?.[
                  record.sentiment as keyof typeof sentimentStyle
                ],
              )}
            >
              <Icon
                name={
                  record.sentiment === "Bullish"
                    ? "ic_rise"
                    : record.sentiment === "Bearish"
                      ? "ic_fall"
                      : "minus"
                }
                size={14}
                color={
                  record.sentiment === "Bullish"
                    ? ospThemes.colors.content_accent1_primary
                    : record.sentiment === "Bearish"
                      ? ospThemes.colors.content_accent2_primary
                      : ospThemes.colors.content_tertiary
                }
              />
              <span>{record.sentiment}</span>
            </div>
          </div>
        ),
      },
    ];
  }, [modalColumns]);

  const columns = useMemo(() => {
    if (variant === "timeline" || variant === "default") {
      return timelineAndDefaultColumns;
    } else if (variant === "web-modal") {
      return webModalColumns;
    } else if (variant === "modal") {
      return modalColumns;
    }
  }, [timelineAndDefaultColumns, webModalColumns, modalColumns, variant]);

  return (
    <div
      data-horizontal-scroll="true"
      className={cn("w-full", className)}
      style={{
        // 动态计算总宽度，保证列宽修改后整体宽度同步

        ["--indicator-table-min-width" as any]: isOver480
          ? "100%"
          : `${columns?.reduce((s, c) => s + (Number((c as any).width) || 0), 0)}px`,
      }}
    >
      <div className={cn(styles.tableContainer, isOver480 ? "desktop" : "")}>
        <VirtualRCTableView
          columns={columns as any}
          data={data}
          rowHeight={48}
        />
      </div>
    </div>
  );
};

export default memo(IndicatorTable);
